import React, { useEffect, useMemo, useState } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";

const ICONS = {
  dialogue: () => (
    <svg viewBox="0 0 72 60" className="w-12 h-10">
      <path d="M6 10 Q6 4 12 4 L52 4 Q58 4 58 10 L58 32 Q58 38 52 38 L26 38 L14 50 L14 38 L12 38 Q6 38 6 32 Z" fill="none" stroke="#1a1a1a" strokeWidth="2.5" strokeLinejoin="round" />
      <ellipse cx="32" cy="22" rx="9" ry="5" fill="none" stroke="#1a1a1a" strokeWidth="2.2" />
      <path d="M26 22 L38 22" stroke="#1a1a1a" strokeWidth="2" />
      <line x1="60" y1="10" x2="70" y2="6" stroke="#1a1a1a" strokeWidth="2.2" strokeLinecap="round" className="svc-dlg-line svc-dlg-line-1" />
      <line x1="60" y1="20" x2="72" y2="20" stroke="#1a1a1a" strokeWidth="2.2" strokeLinecap="round" className="svc-dlg-line svc-dlg-line-2" />
      <line x1="60" y1="30" x2="70" y2="34" stroke="#1a1a1a" strokeWidth="2.2" strokeLinecap="round" className="svc-dlg-line svc-dlg-line-3" />
    </svg>
  ),
  backgrounds: () => (
    <svg viewBox="0 0 80 50" className="w-14 h-9">
      {/* Sun — rises from left, sets on right (first half of loop) */}
      <g>
        <animateTransform
          attributeName="transform"
          type="translate"
          values="5 47; 12 32; 20 15; 40 4; 60 15; 68 32; 75 47; 75 47"
          keyTimes="0; 0.0625; 0.15; 0.25; 0.35; 0.4375; 0.4875; 1"
          dur="9s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="opacity"
          values="0; 1; 1; 0; 0"
          keyTimes="0; 0.02; 0.4875; 0.5; 1"
          dur="9s"
          repeatCount="indefinite"
        />
        <circle cx="0" cy="0" r="4" fill="#f5d460" stroke="#1a1a1a" strokeWidth="1.4" />
        <g stroke="#1a1a1a" strokeWidth="1" strokeLinecap="round" opacity="0.9">
          <line x1="0" y1="-7" x2="0" y2="-5.5" />
          <line x1="0" y1="5.5" x2="0" y2="7" />
          <line x1="-7" y1="0" x2="-5.5" y2="0" />
          <line x1="5.5" y1="0" x2="7" y2="0" />
          <line x1="-5" y1="-5" x2="-4" y2="-4" />
          <line x1="4" y1="4" x2="5" y2="5" />
          <line x1="-5" y1="5" x2="-4" y2="4" />
          <line x1="4" y1="-4" x2="5" y2="-5" />
        </g>
      </g>

      {/* Moon — rises and sets during the second half */}
      <g>
        <animateTransform
          attributeName="transform"
          type="translate"
          values="75 47; 75 47; 5 47; 12 32; 20 15; 40 4; 60 15; 68 32; 75 47"
          keyTimes="0; 0.4875; 0.5; 0.5625; 0.65; 0.75; 0.85; 0.9375; 1"
          dur="9s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="opacity"
          values="0; 0; 1; 1; 0"
          keyTimes="0; 0.5; 0.52; 0.985; 1"
          dur="9s"
          repeatCount="indefinite"
        />
        <circle cx="0" cy="0" r="4" fill="#f4f1ea" stroke="#1a1a1a" strokeWidth="1.4" />
        {/* Crescent bite — matches the parent circle sky color */}
        <circle cx="1.6" cy="-0.5" r="3.3" fill="#7fb3d5" />
      </g>

      {/* Mountains — drawn last so they cover celestial bodies below the horizon */}
      <path
        d="M-2 52 L-2 46 L22 18 L34 32 L50 8 L66 28 L78 14 L82 46 L82 52 Z"
        fill="#1a1a1a"
      />
    </svg>
  ),
  design: () => (
    <svg viewBox="0 0 70 70" className="w-12 h-12">
      <g className="svc-boom">
        <path d="M35 4 L40 22 L58 14 L46 30 L66 33 L46 38 L58 56 L40 48 L35 66 L30 48 L12 56 L24 38 L4 33 L24 30 L12 14 L30 22 Z" fill="#1a1a1a" stroke="#1a1a1a" strokeWidth="1.5" strokeLinejoin="round" />
        <text x="35" y="40" textAnchor="middle" fontSize="11" fontWeight="800" fill="#f5e26a" fontFamily="Fraunces, serif">BOOM</text>
      </g>
    </svg>
  ),
  mix: () => (
    <svg viewBox="0 0 80 50" className="w-12 h-9">
      {[10, 28, 46, 64].map((x, i) => (
        <g key={x}>
          <line x1={x} y1="2" x2={x} y2="48" stroke="#1a1a1a" strokeWidth="2.5" />
          <rect
            x={x - 5}
            y={10 + i * 6}
            width="10"
            height="6"
            fill="#1a1a1a"
            className={`svc-mix-knob svc-mix-knob-${i + 1}`}
          />
        </g>
      ))}
    </svg>
  ),
  delivery: () => (
    <svg viewBox="0 0 60 56" className="w-10 h-10">
      <g className="svc-file">
        <path d="M10 4 L40 4 L52 16 L52 52 L10 52 Z" fill="#f4f1ea" stroke="#1a1a1a" strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M40 4 L40 16 L52 16" fill="none" stroke="#1a1a1a" strokeWidth="2.5" strokeLinejoin="round" />
        <line x1="18" y1="28" x2="44" y2="28" stroke="#1a1a1a" strokeWidth="2" />
        <line x1="18" y1="36" x2="44" y2="36" stroke="#1a1a1a" strokeWidth="2" />
        <line x1="18" y1="44" x2="38" y2="44" stroke="#1a1a1a" strokeWidth="2" />
      </g>
      {/* Cursor that taps the file */}
      <g className="svc-cursor">
        <path
          d="M30 28 L30 44 L34 40.5 L37 46 L39.5 45 L36.5 39.5 L42 39.5 Z"
          fill="#1a1a1a"
          stroke="#f4f1ea"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </g>
      {/* Click ripple */}
      <circle
        cx="30"
        cy="30"
        r="3"
        fill="none"
        stroke="#1a1a1a"
        strokeWidth="1.5"
        className="svc-click-ripple"
      />
    </svg>
  ),
};

const SVC_ANIM_CSS = `
  @keyframes svcDlgBlink {
    0%, 55% { opacity: 1; }
    63%, 70% { opacity: 0.12; }
    78%, 100% { opacity: 1; }
  }
  .svc-dlg-line { animation: svcDlgBlink 1.8s ease-in-out infinite; }
  .svc-dlg-line-2 { animation-delay: 0.18s; }
  .svc-dlg-line-3 { animation-delay: 0.36s; }

  @keyframes svcSunSpin { to { transform: rotate(360deg); } }
  .svc-sun {
    transform-box: fill-box;
    transform-origin: center;
    animation: svcSunSpin 7s linear infinite;
  }

  @keyframes svcBoomBurst {
    0%, 100% { transform: scale(1) rotate(0deg); }
    30%      { transform: scale(1.22) rotate(4deg); }
    45%      { transform: scale(0.9) rotate(-3deg); }
    60%      { transform: scale(1.1) rotate(2deg); }
    75%      { transform: scale(0.98) rotate(-1deg); }
  }
  .svc-boom {
    transform-box: fill-box;
    transform-origin: center;
    animation: svcBoomBurst 2.4s ease-in-out infinite;
  }

  @keyframes svcMixMove {
    0%, 100% { transform: translateY(-6px); }
    50%      { transform: translateY(10px); }
  }
  .svc-mix-knob {
    transform-box: fill-box;
    animation: svcMixMove 2.6s ease-in-out infinite;
  }
  .svc-mix-knob-1 { animation-delay: 0s; }
  .svc-mix-knob-2 { animation-delay: 0.25s; }
  .svc-mix-knob-3 { animation-delay: 0.5s; }
  .svc-mix-knob-4 { animation-delay: 0.75s; }

  @keyframes svcFileClick {
    0%, 74%, 100% { transform: scale(1); }
    82%           { transform: scale(0.92); }
    90%           { transform: scale(1.03); }
  }
  .svc-file {
    transform-box: fill-box;
    transform-origin: center;
    animation: svcFileClick 2.6s ease-in-out infinite;
  }

  @keyframes svcCursorTap {
    0%, 60% { opacity: 0; transform: translate(3px, 3px); }
    70%     { opacity: 1; transform: translate(0, 0); }
    82%     { opacity: 1; transform: translate(-1px, -1px); }
    92%     { opacity: 1; transform: translate(0, 0); }
    100%    { opacity: 0; transform: translate(0, 0); }
  }
  .svc-cursor {
    transform-box: fill-box;
    transform-origin: 0 0;
    animation: svcCursorTap 2.6s ease-in-out infinite;
  }

  @keyframes svcClickRipple {
    0%, 74%, 100% { opacity: 0; r: 3; }
    82%           { opacity: 0.9; r: 3; }
    95%           { opacity: 0; r: 12; }
  }
  .svc-click-ripple {
    animation: svcClickRipple 2.6s ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .svc-dlg-line, .svc-sun, .svc-boom, .svc-mix-knob,
    .svc-file, .svc-cursor, .svc-click-ripple {
      animation: none;
    }
  }
`;

const SERVICES = [
  { id: "dialogue", title: "DIALOGUE EDITING", blurb: "Clean, Clear. You wrote a good script, let's hear it.", icon: ICONS.dialogue, color: "#ff5fa2" },
  { id: "backgrounds", title: "BACKGROUNDS", blurb: "Textures that pull you in.", icon: ICONS.backgrounds, color: "#7fb3d5" },
  { id: "design", title: "SOUND DESIGN", blurb: "Who doesn't love explosions or creepy door creaks?", icon: ICONS.design, color: "#f5e26a" },
  { id: "mix", title: "MIXING", blurb: "Great sound in any theater, or headphones.", icon: ICONS.mix, color: "#ff5fa2" },
  { id: "delivery", title: "DELIVERY", blurb: "Festivals, DCP, and streaming.", icon: ICONS.delivery, color: "#7fb3d5" },
];

const TABS = [
  {
    id: "film",
    label: "FILM",
    accent: "#e8472b",
    turnaround: (m) => Math.max(1, Math.ceil((m / 10) * 4)),
    options: [
      { id: "dialogue", label: "DIALOGUE ONLY", rate: 80 },
      { id: "full", label: "FULL 5.1 SOUND DESIGN / MIX", rate: 130 },
    ],
  },
  {
    id: "doc",
    label: "DOCUMENTARY",
    accent: "#f5d460",
    turnaround: (m) => Math.max(1, Math.ceil((m / 10) * 3)),
    options: [
      { id: "dialogue", label: "DIALOGUE ONLY", rate: 50 },
      { id: "full", label: "FULL 5.1 SOUND DESIGN / MIX", rate: 90 },
    ],
  },
  {
    id: "ads",
    label: "ADS",
    accent: "#7fb3d5",
    turnaround: () => 1,
    options: [{ id: "full", label: "FULL 5.1 SOUND DESIGN / MIX", rate: 150 }],
  },
];

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// Cinematic clapperboard-inspired calculator
const Calculator = () => {
  const [tabId, setTabId] = useState("film");
  const tab = TABS.find((t) => t.id === tabId);
  const [optionId, setOptionId] = useState("full");
  const [minutes, setMinutes] = useState([15]);

  useEffect(() => {
    if (!tab.options.find((o) => o.id === optionId)) setOptionId(tab.options[0].id);
  }, [tab, optionId]);

  const selectedOption = tab.options.find((o) => o.id === optionId) || tab.options[0];
  const total = useMemo(() => (selectedOption ? selectedOption.rate * minutes[0] : 0), [selectedOption, minutes]);
  const days = useMemo(() => tab.turnaround(minutes[0]), [tab, minutes]);

  return (
    <div
      data-testid="calc-card"
      className="relative w-full"
      style={{
        background: "linear-gradient(180deg, #1c1c1c 0%, #0e0e0e 100%)",
        border: "1px solid #2a2a2a",
        boxShadow:
          "0 0 0 1px rgba(245,212,96,0.10) inset, 0 22px 60px rgba(0,0,0,0.55), 0 2px 0 rgba(255,255,255,0.04) inset",
        borderRadius: "4px",
      }}
    >
      {/* Clapperboard stripe header */}
      <div className="flex items-center gap-0 h-7 border-b border-[#2a2a2a]">
        {Array.from({ length: 16 }).map((_, i) => (
          <span
            key={i}
            className="flex-1 h-full"
            style={{ background: i % 2 === 0 ? "#f4f1ea" : "#1a1a1a", transform: "skewX(-18deg)" }}
          />
        ))}
      </div>

      {/* Title */}
      <div className="flex items-center justify-between px-6 md:px-8 pt-5 pb-3">
        <h3 className="font-display font-black text-2xl md:text-3xl text-[#f4f1ea] tracking-tight">
          RATE CALCULATOR
        </h3>
      </div>

      {/* Tabs */}
      <div className="px-6 md:px-8 pt-1 pb-4">
        <div className="font-mono-indie text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-2">
          PROJECT TYPE
        </div>
        <div className="flex flex-wrap items-stretch border border-[#2a2a2a]" data-testid="calc-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`calc-tab-${t.id}`}
              onClick={() => setTabId(t.id)}
              className={`flex-1 min-w-[100px] px-3 py-2.5 font-mono-indie font-semibold text-[10px] sm:text-[11px] tracking-[0.22em] border-r border-[#2a2a2a] last:border-r-0 transition-colors ${
                tabId === t.id ? "text-[#1a1a1a]" : "text-[#cbb88a] hover:text-[#f4f1ea] bg-[#181818]"
              }`}
              style={tabId === t.id ? { background: t.accent } : {}}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Options */}
      <div className="px-6 md:px-8 py-4 border-t border-[#2a2a2a]">
        <div className="font-mono-indie text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-3">
          PICK ONE
        </div>
        <RadioGroup
          data-testid="calc-options"
          value={optionId}
          onValueChange={setOptionId}
          className="space-y-0"
        >
          {tab.options.map((o) => {
            const active = o.id === optionId;
            return (
              <label
                key={o.id}
                data-testid={`calc-option-${o.id}`}
                className={`flex items-center gap-4 cursor-pointer py-3 border-b border-dashed border-[#2a2a2a] last:border-b-0 transition-colors ${
                  active ? "" : "opacity-70 hover:opacity-100"
                }`}
              >
                <RadioGroupItem
                  value={o.id}
                  data-testid={`calc-radio-${o.id}`}
                  className="h-4 w-4 border border-[#cbb88a] shrink-0"
                  style={active ? { color: tab.accent, borderColor: tab.accent } : {}}
                />
                <span className="font-display font-black text-[13px] md:text-[14px] tracking-[0.05em] text-[#f4f1ea] flex-1 min-w-0">
                  {o.label}
                </span>
                <span
                  className="font-mono-indie font-semibold text-[12px] md:text-[13px] tracking-[0.05em] whitespace-nowrap"
                  style={{
                    color: active ? tab.accent : "#cbb88a",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  ${o.rate}/MIN
                </span>
              </label>
            );
          })}
        </RadioGroup>
      </div>

      {/* Runtime slider */}
      <div className="px-6 md:px-8 py-5 border-t border-[#2a2a2a]">
        <div className="flex items-baseline justify-between mb-3">
          <span className="font-mono-indie text-[10px] tracking-[0.25em] text-[#7a7a7a]">
            RUNTIME
          </span>
          <span
            data-testid="calc-minutes-display"
            className="font-mono-indie font-semibold text-[13px] tracking-[0.12em]"
            style={{ color: tab.accent, fontVariantNumeric: "tabular-nums" }}
          >
            {String(minutes[0]).padStart(3, "0")} MIN
          </span>
        </div>
        <Slider
          data-testid="calc-slider"
          value={minutes}
          onValueChange={setMinutes}
          min={1}
          max={120}
          step={1}
          className="mt-1"
        />
        <div className="flex justify-between font-mono-indie text-[9px] tracking-[0.2em] text-[#5a5a5a] mt-2">
          <span>SHORT</span>
          <span>FEATURE</span>
        </div>
      </div>

      {/* Readouts row */}
      <div className="grid grid-cols-2 border-t border-[#2a2a2a]">
        <div className="px-6 md:px-8 py-4">
          <div className="font-mono-indie text-[10px] tracking-[0.25em] text-[#7a7a7a]">
            TURNAROUND
          </div>
          <div
            data-testid="calc-days"
            className="font-display font-black text-xl md:text-2xl text-[#f4f1ea] mt-1"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            ~{days} {days === 1 ? "DAY" : "DAYS"}
          </div>
        </div>
        <div className="px-6 md:px-8 py-4 border-l border-[#2a2a2a]">
          <div className="font-mono-indie text-[10px] tracking-[0.25em] text-[#7a7a7a]">
            TIMECODE
          </div>
          <div
            className="font-mono-indie text-base md:text-lg text-[#ff453a] mt-1 tracking-[0.12em]"
            style={{ fontVariantNumeric: "tabular-nums", textShadow: "0 0 8px rgba(255,69,58,0.5)" }}
          >
            00:{String(minutes[0]).padStart(2, "0")}:00:00
          </div>
        </div>
      </div>

      {/* TOTAL — big LED budget */}
      <div
        className="px-6 md:px-8 py-6 border-t border-[#2a2a2a] flex items-center justify-between"
        style={{
          background:
            "linear-gradient(180deg, #131313 0%, #050505 100%)",
          borderTop: "1px solid #6b4a2a",
        }}
      >
        <div>
          <div className="font-mono-indie text-[10px] tracking-[0.28em] text-[#cbb88a]">
            ESTIMATED BUDGET
          </div>
          <div className="font-mono-indie text-[10px] tracking-[0.18em] text-[#5a5a5a] mt-2">
            * ballpark · real bid depends on the project
          </div>
        </div>
        <div
          data-testid="calc-total"
          className="font-display font-black text-3xl sm:text-4xl md:text-[44px] tracking-tight"
          style={{
            color: tab.accent,
            fontVariantNumeric: "tabular-nums",
            textShadow: `0 0 16px ${tab.accent}55`,
          }}
        >
          {fmt(total)}
        </div>
      </div>

      {/* Bottom clapperboard stripe */}
      <div className="flex items-center gap-0 h-7 border-t border-[#2a2a2a]">
        {Array.from({ length: 16 }).map((_, i) => (
          <span
            key={i}
            className="flex-1 h-full"
            style={{ background: i % 2 === 0 ? "#1a1a1a" : "#f4f1ea", transform: "skewX(-18deg)" }}
          />
        ))}
      </div>
    </div>
  );
};

export const ServicesAndCalc = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#c9c8c4] py-14 md:py-20"
    >
      <style>{SVC_ANIM_CSS}</style>
      <div className="mx-auto max-w-[1400px] px-5 sm:px-6 md:px-10">
        <div className="text-center mb-10">
          <h2
            data-testid="services-headline"
            className="inline-block font-display font-black text-2xl md:text-3xl tracking-tight relative"
          >
            HOW WE CAN HELP
            <span className="block w-16 h-[3px] bg-foreground mt-2 mx-auto" />
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* LEFT — vertical services list */}
          <div className="lg:col-span-5 min-w-0 space-y-7">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.id} data-testid={`service-${s.id}`} className="flex items-center gap-5">
                  <div
                    className="shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center"
                    style={{ background: s.color }}
                  >
                    <Icon />
                  </div>
                  <div>
                    <h3 className="font-display font-black text-[15px] md:text-[16px] tracking-[0.1em]">
                      {s.title}
                    </h3>
                    <p className="font-mono-indie text-[12px] md:text-[13px] mt-1.5 text-[#3a3a3a] leading-snug">
                      {s.blurb}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT — cinematic rate calculator */}
          <div className="lg:col-span-7 min-w-0 w-full">
            <Calculator />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesAndCalc;
